import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, change, formValueSelector, isDirty } from 'redux-form';
import { withRouter } from 'react-router';
import Toggle from 'react-toggle';
import { ButtonV2, RenderField } from '@zola/zola-ui';
import { SortableElement } from 'react-sortable-hoc';

import { showModal, hideModal } from '../../../../actions/ModalActions';
import FileInput from '../../../common/form/FileInput';
import { tempUpload, saveCrop } from '../../../../actions/website/FileUploadActions';
import { RenderSubmitButton } from '../../../common/form';
import * as Validations from '../../../common/form/commonValidations';
import { updatePage, updatePageSilently } from '../../../../actions/website/PageActions';
import { updateWeddingAccount } from '../../../../actions/WeddingActions';
import DragHandle from '../../../common/ui/DragHandle';
import EditPageAddEntity from './EditPageCommon/EditPageAddEntity';
import { EntityName } from './EditPageCommon/EditPageSortableList';

import featureFlag from '../../../../util/featureFlags';

const isCmsPageFormV2 = featureFlag.get('isCmsPageFormV2');
const isEditPageSortableList = featureFlag.get('isEditPageSortableList');

const HomepageSection = SortableElement(({ homepageSection, onEdit, onDelete }) => {
  const noPropagate = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  return (
    <li className="sortable-item editable-entity" onClick={() => onEdit(homepageSection)}>
      <div className="col-sm-1">
        <DragHandle tooltip="Drag to reorder" />
      </div>
      <div className="col-sm-8">
        <span className="title-v2">{homepageSection.title}</span>
      </div>
      <div className="col-sm-3 entity-actions" onClick={noPropagate}>
        <button type="button" onClick={() => onEdit(homepageSection)} title="Edit Story" className="btn btn-icon with-tooltip no-tooltip-on-mobile home-tooltip" data-tooltip="Edit"><i className="glyphicon glyphicon-pencil" /></button>
        <button type="button" onClick={() => onDelete(homepageSection)} title="Delete Story" className="btn btn-icon with-tooltip no-tooltip-on-mobile home-tooltip" data-tooltip="Delete"><i className="glyphicon glyphicon-trash" /></button>
      </div>
    </li>
  );
});

const StoryDetails = (entity) => {
  const story = entity || {};
  const { title } = story;
  return (
    <EntityName name={title} />
  )
}

HomepageSection.propTypes = {
  homepageSection: PropTypes.shape({}).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number
};

class HomepageForm extends Component {
  constructor(props) {
    super(props);
    this.removePhoto = this.removePhoto.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.routerWillLeave = this.routerWillLeave.bind(this);
    this.onSubmitFormSilently = this.onSubmitFormSilently.bind(this);

    const { headerImageUrl, footerImageUrl, headerFilePreview, footerFilePreview } = this.props;
    this.state = {
      hasHeaderImage: !!(headerImageUrl || headerFilePreview.cloudfront_url),
      hasFooterImage: !!(footerImageUrl || footerFilePreview.cloudfront_url)
    };
  }

  componentWillReceiveProps(nextProps) {
    const { onUploadSuccess, onCropSuccess, onAutoSave, footerFilePreview, headerFilePreview, page } = this.props;
    if (nextProps.headerFilePreview.s3_key && (nextProps.headerFilePreview.s3_key !== headerFilePreview.s3_key)) {
      onUploadSuccess(nextProps.headerFilePreview, 'header_image');
    } else if (nextProps.footerFilePreview.s3_key && (nextProps.footerFilePreview.s3_key !== footerFilePreview.s3_key)) {
      onUploadSuccess(nextProps.footerFilePreview, 'footer_image');
    } else if (nextProps.headerFilePreview.uuid && (headerFilePreview.uuid !== nextProps.headerFilePreview.uuid)) {
      this.setState({ hasHeaderImage: true });
      onCropSuccess(nextProps.headerFilePreview, 'header_image');
    } else if (nextProps.footerFilePreview.uuid && (footerFilePreview.uuid !== nextProps.footerFilePreview.uuid)) {
      this.setState({ hasFooterImage: true });
      onCropSuccess(nextProps.footerFilePreview, 'footer_image');
    } else if (nextProps.formValues && (nextProps.formValues.header_image_id !== page.header_image_id)) {
      const newPageValues = { ...page, ...nextProps.formValues };
      if (isCmsPageFormV2 && (nextProps.page === this.props.page)) {
        onAutoSave(newPageValues);
      } else if (!isCmsPageFormV2) {
        onAutoSave(newPageValues);
      }
    } else if (nextProps.formValues && (nextProps.formValues.footer_image_id !== page.footer_image_id)) {
      const newPageValues = { ...page, ...nextProps.formValues };
      if (isCmsPageFormV2 && (nextProps.page === this.props.page)) {
        onAutoSave(newPageValues);
      } else if (!isCmsPageFormV2) {
        onAutoSave(newPageValues);
      }
    }
  }

  componentDidUpdate() {
    const { dirty } = this.props;
    if (dirty && !isCmsPageFormV2) {
        this.props.router.setRouteLeaveHook(
        this.props.routes[this.props.routes.length - 1], this.routerWillLeave
      );
    }
    if (isCmsPageFormV2) {
      this.props.router.setRouteLeaveHook(
      this.props.routes[this.props.routes.length - 1], this.routerWillLeave
    );
  }
  }

  onSubmit(values) {
    const { handleSubmit, isFormDirty, valid } = this.props;

    if (!isCmsPageFormV2) {
      const submitReturn = handleSubmit(values);
      submitReturn.then(this.handleSuccess);
    }

    if (isCmsPageFormV2) {
      if(isFormDirty && valid) handleSubmit(values)
    }
  }

  onSubmitFormSilently(values) {
    const { onUpdatePageSilently, isFormDirty, valid } = this.props;

    if (isFormDirty && valid) onUpdatePageSilently(values)
  }

  onToggle() {
    const { account, toggleSeal } = this.props;
    const accountUpdate = Object.assign({}, account, { enable_seal: !account.enable_seal });
    toggleSeal(accountUpdate);
  }

  removePhoto(location) {
    const { deletePhoto } = this.props;
    if (location === 'header_image') {
      this.setState({ hasHeaderImage: false });
    } else if (location === 'footer_image') {
      this.setState({ hasFooterImage: false });
    }
    deletePhoto(location);
  }

  handleSuccess() {
    this.setState({ successfulSave: true });
    setTimeout(() => this.setState({ successfulSave: false }), 1000);
  }

  routerWillLeave() {
    const { dirty, isFormDirty } = this.props;
    if (isFormDirty && isCmsPageFormV2) this.onSubmit();
    if (dirty && !isCmsPageFormV2) return 'You have unsaved changes. Hit "Cancel" to save them or risk losing your work.';
    return undefined;
  }

  render() {
    const {
      valid,
      pristine,
      onUpload,
      headerImageUrl,
      footerImageUrl,
      headerFilePreview,
      footerFilePreview,
      account,
      homepageSections,
      onEdit,
      onDelete,
      openModal,
      orderedIds,
      handleSubmit,
     } = this.props;
    const { successfulSave } = this.state;
    const hfilePreview = (headerFilePreview.cloudfront_url ? headerFilePreview.cloudfront_url : headerImageUrl);
    const ffilePreview = (footerFilePreview.cloudfront_url ? footerFilePreview.cloudfront_url : footerImageUrl);

    return (
      <form onSubmit={this.onSubmit} id="home-page-form" noValidate className={`${isCmsPageFormV2 ? 'home-page-form' : ''} ${isEditPageSortableList ? 'home-page-form-v2' : ''}`}>
        <div>
          <Field
            name="nav_title"
            type="text"
            label="Page Title"
            id="cms-page-title"
            component={RenderField}
            required
            maxChars={40}
            validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
            showCharacterCount
            onBlur={isCmsPageFormV2 && handleSubmit(this.onSubmitFormSilently)}
            className="page-title-field"
          />
          <Field
            name="title"
            type="text"
            label="Welcome Message"
            id="homepage-welcome-message"
            placeholder="We're so excited to celebrate with you!"
            component={RenderField}
            maxChars={150}
            validate={Validations.MAX_LENGTH_150}
            showCharacterCount
            onBlur={isCmsPageFormV2 && handleSubmit(this.onSubmitFormSilently)}
            className="welcome-message"
          />
          {!isCmsPageFormV2 && <RenderSubmitButton valid={valid} pristine={pristine} disableWhenPristine showSuccess={successfulSave} className="margin-bottom-2x" />}
        </div>
        <div className="header-image-label form-label">Main Image <small>(max file size 5 MB)</small></div>
        <div className="header-image margin-bottom-2x">
          <div className={`header-image-preview${this.state.hasHeaderImage ? ' has-preview' : ''}`}>
            <FileInput
              filePreview={this.state.hasHeaderImage ? hfilePreview : undefined}
              inputId="header_image"
              onSelect={file => onUpload(file, 'header_image')}
              labelText={isEditPageSortableList ? 'Upload Your Image' : ''}
            />
          </div>
          <div className={`header-image-upload${this.state.hasHeaderImage ? ' has-preview' : ''}`}>
            <div className="actions text-center">
              {this.state.hasHeaderImage && (
                <ButtonV2 type="button" variant="tertiary" onClick={() => this.removePhoto('header_image')}>Delete</ButtonV2>
              )}
            </div>
          </div>
        </div>
        {isCmsPageFormV2 && (
          <EditPageAddEntity
            entityName="Story"
            entitiesListTitle="Your Stories"
            pageType="home"
            orderedIds={orderedIds}
            entities={homepageSections}
            onAdd={openModal}
            onEdit={onEdit}
            onDelete={onDelete}
            entityDetail={StoryDetails}
          >
            <ul className="sortable-list">
              {homepageSections && orderedIds.map((homepageSectionId, index) => (
                <HomepageSection
                  form="story_form"
                  key={homepageSectionId}
                  homepageSection={homepageSections[homepageSectionId]}
                  index={index}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </EditPageAddEntity>
        )}
        <div className="header-image-label form-label">Bottom Image <small>(max file size 5 MB)</small></div>
        <div className="header-image margin-bottom">
          <div className={`header-image-preview${this.state.hasFooterImage ? ' has-preview' : ''}`}>
            <FileInput
              filePreview={this.state.hasFooterImage ? ffilePreview : undefined}
              inputId="footer_image"
              onSelect={file => onUpload(file, 'footer_image')}
              labelText={isEditPageSortableList ? 'Upload Your Image' : ''}
            />
          </div>
          <div className={`header-image-upload${this.state.hasFooterImage ? ' has-preview' : ''}`}>
            <div className="actions text-center">
              {this.state.hasFooterImage && (
                <ButtonV2 type="button" variant="tertiary" onClick={() => this.removePhoto('footer_image')}>Delete</ButtonV2>
              )}
            </div>
          </div>
        </div>
        <div className="form-group enable-seal visible-xs visible-sm">
          <span className="toggle-description form-label">Monogram Footer
            <div className="with-tooltip" data-tooltip="A few of our website designs include a monogram at the base of the website pages. If your selected design has this detail, you can choose to make it visible or hidden here."><i className="zolaicon zolaicon-info" />
            </div>
          </span>
          <Toggle
            checked={account.enable_seal}
            onChange={this.onToggle}
            icons={{ checked: <span className="toggle-label checked">Visible</span>, unchecked: <span className="toggle-label unchecked">Hidden</span> }}
          />
        </div>
        <div className="form-group enable-seal visible-md visible-lg">
          <span className="toggle-description form-label">
            <div className="with-tooltip" data-tooltip="A few of our website designs include a monogram at the base of the website pages. If your selected design has this detail, you can choose to make it visible or hidden here.">
              <span className="monogram">Monogram Footer</span>
              <Toggle
                checked={account.enable_seal}
                onChange={this.onToggle}
                icons={{ checked: <span className="toggle-label checked monogram-toggle">Visible</span>, unchecked: <span className="toggle-label unchecked monogram-toggle">Hidden</span> }}
              />
            </div>
          </span>
        </div>
      </form>
    );
  }
}

HomepageForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  isFormDirty: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  headerImageUrl: PropTypes.string,
  footerImageUrl: PropTypes.string,
  headerFilePreview: PropTypes.shape({ uuid: PropTypes.string, s3_key: PropTypes.string }),
  footerFilePreview: PropTypes.shape({ uuid: PropTypes.string, s3_key: PropTypes.string }),
  onUpload: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onCropSuccess: PropTypes.func,
  onAutoSave: PropTypes.func,
  deletePhoto: PropTypes.func,
  account: PropTypes.shape({}),
  toggleSeal: PropTypes.func,
  page: PropTypes.shape({}),
  formValues: PropTypes.shape({}),
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func,
  }),
};

const selector = formValueSelector('homepageSection');

const mapStateToProps = state => ({
  initialValues: state.website.pages.byId[state.website.pages.typeToId.HOME],
  formValues: selector(state, 'description', 'header_image_id', 'header_image_url', 'footer_image_id', 'footer_image_url'),
  headerFilePreview: state.website.entities.filePreview.header_image || {},
  footerFilePreview: state.website.entities.filePreview.footer_image || {},
  headerImageUrl: state.website.pages.byId[state.website.pages.typeToId.HOME].header_image_url,
  footerImageUrl: state.website.pages.byId[state.website.pages.typeToId.HOME].footer_image_url,
  page: state.website.pages.byId[state.website.pages.typeToId.HOME],
  account: state.wedding.account,
  isFormDirty: isDirty('homepageSection')(state),
});

const mapDispatchToProps = dispatch => ({
  onSubmit: pageView => dispatch(updatePage(pageView)),
  onUpdatePageSilently: (pageView) => dispatch(updatePageSilently(pageView)),
  onUpload: (file, loc) => dispatch(tempUpload(file, loc)),
  onUploadSuccess: (image, loc) => {
    dispatch(showModal('CROP_MODAL', { imageUrl: image.url, onCropSelect: crop => dispatch(saveCrop(image.s3_bucket, image.s3_key, crop, image.content_type, loc)) }));
  },
  onCropSuccess: (image, loc) => {
    dispatch(hideModal());
    dispatch(change('homepageSection', `${loc}_id`, image.uuid));
    dispatch(change('homepageSection', `${loc}_url`, image.cloudfront_url));
  },
  onAutoSave: pageView => {
    if (isCmsPageFormV2) dispatch(updatePageSilently(pageView))
    if (!isCmsPageFormV2) dispatch(updatePage(pageView))
  },
  deletePhoto: (location) => {
    if (location) {
      dispatch(change('homepageSection', `${location}_id`, ''));
      dispatch(change('homepageSection', `${location}_url`, ''));
    }
  },
  toggleSeal: account => dispatch(updateWeddingAccount(account))
});

let HomepageFormWithRouter = withRouter(HomepageForm);
HomepageFormWithRouter = reduxForm({
  form: 'homepageSection',
  enableReinitialize: !isCmsPageFormV2
})(HomepageFormWithRouter);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HomepageFormWithRouter);



// WEBPACK FOOTER //
// ./src/components/manage/website/entities/HomepageForm.jsx